import { useState, useMemo } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useAllMilestones } from '@/hooks/useAllMilestones';
import { useJobs } from '@/hooks/useJobs';
import { useAuth } from '@/lib/auth';
import { groupMilestonesByDate, getMilestoneIndicatorColor } from '@/lib/milestoneCalendarUtils';
import { MilestoneDetailsDialog } from './MilestoneDetailsDialog';
import { startOfMonth, endOfMonth, addMonths, subMonths, format } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays, List } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MilestoneWithJob } from '@/lib/types/milestone';

type ActionOwnerFilter = 'all' | 'vendor' | 'client';

type ViewMode = 'calendar' | 'list';

export const MilestoneCalendar = () => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedJobId, setSelectedJobId] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedMilestones, setSelectedMilestones] = useState<MilestoneWithJob[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [actionOwnerFilter, setActionOwnerFilter] = useState<ActionOwnerFilter>('all');
  const [hidePastDates, setHidePastDates] = useState(false);

  const startDate = startOfMonth(currentMonth);
  const endDate = endOfMonth(currentMonth);

  const { data: milestones = [], isLoading, error } = useAllMilestones({
    startDate,
    endDate,
    jobIdFilter: selectedJobId === 'all' ? undefined : selectedJobId,
  });

  const { jobs = [] } = useJobs();

  // Create a map of jobs by ID for quick lookup
  const jobsById = useMemo(() => {
    const map = new Map();
    jobs.forEach(job => map.set(job.id, job));
    return map;
  }, [jobs]);

  // Enrich milestones with job data
  const enrichedMilestones = useMemo(() => {
    return milestones.map(milestone => ({
      ...milestone,
      job_title: jobsById.get(milestone.job_id)?.title || 'Unknown Job',
      job_public_id: jobsById.get(milestone.job_id)?.public_id || '',
      job_status: jobsById.get(milestone.job_id)?.status || '',
    } as MilestoneWithJob));
  }, [milestones, jobsById]);

  // Apply action owner and past date filters
  const filteredMilestones = useMemo(() => {
    return enrichedMilestones.filter(milestone => {
      // Filter by action owner
      if (actionOwnerFilter !== 'all' && milestone.action_owner !== actionOwnerFilter) {
        return false;
      }
      
      // Filter out past dates if checkbox is checked
      if (hidePastDates) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(milestone.due_date);
        if (dueDate < today) {
          return false;
        }
      }
      
      return true;
    });
  }, [enrichedMilestones, actionOwnerFilter, hidePastDates]);

  const milestonesGroupedByDate = useMemo(
    () => groupMilestonesByDate(filteredMilestones),
    [filteredMilestones]
  );

  const handlePreviousMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    const today = new Date();
    const twelveMonthsFromNow = addMonths(today, 12);
    
    if (newMonth >= today || format(newMonth, 'yyyy-MM') === format(today, 'yyyy-MM')) {
      setCurrentMonth(newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    const twelveMonthsFromNow = addMonths(new Date(), 12);
    
    if (newMonth <= twelveMonthsFromNow) {
      setCurrentMonth(newMonth);
    }
  };

  const handleDayClick = (date: Date | undefined) => {
    if (!date) return;
    
    const dateKey = date.toISOString().split('T')[0];
    const dayMilestones = milestonesGroupedByDate.get(dateKey) || [];
    
    if (dayMilestones.length > 0) {
      setSelectedDate(date);
      setSelectedMilestones(dayMilestones);
      setDialogOpen(true);
    }
  };

  const today = new Date();
  const twelveMonthsFromNow = addMonths(today, 12);
  const canGoPrevious = currentMonth > today;
  const canGoNext = currentMonth < twelveMonthsFromNow;

  // Sort milestones by date for list view
  const sortedMilestones = useMemo(() => {
    return [...filteredMilestones].sort((a, b) => 
      new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
    );
  }, [filteredMilestones]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle>Milestone Calendar</CardTitle>
            
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('calendar')}
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Calendar
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4 mr-2" />
                List
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Month Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousMonth}
                disabled={!canGoPrevious}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[140px] text-center font-medium">
                {format(currentMonth, 'MMMM yyyy')}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                disabled={!canGoNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Job Filter */}
            <Select value={selectedJobId} onValueChange={setSelectedJobId}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by job" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map((job) => (
                  <SelectItem key={job.id} value={job.id}>
                    {job.title} ({job.public_id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Action Owner Filter */}
            <Select value={actionOwnerFilter} onValueChange={(value: ActionOwnerFilter) => setActionOwnerFilter(value)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="vendor">Vendor Actions</SelectItem>
                <SelectItem value="client">Client Actions</SelectItem>
              </SelectContent>
            </Select>

            {/* Hide Past Dates Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="hidePast" 
                checked={hidePastDates}
                onCheckedChange={(checked) => setHidePastDates(checked === true)}
              />
              <Label htmlFor="hidePast" className="text-sm cursor-pointer whitespace-nowrap">
                Hide past dates
              </Label>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-primary bg-primary/20" />
              <span>Your Responsibility</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border border-muted-foreground/20 bg-muted" />
              <span>Others' Responsibility</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-green-500 bg-green-500/20" />
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-destructive bg-destructive/20" />
              <span>Overdue</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar or List View */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-[400px] w-full" />
          </CardContent>
        </Card>
      ) : viewMode === 'calendar' ? (
        <Card>
          <CardContent className="p-6 flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDayClick}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              className="rounded-md border pointer-events-auto"
              modifiers={{
                hasMilestones: (date) => {
                  const dateKey = date.toISOString().split('T')[0];
                  return milestonesGroupedByDate.has(dateKey);
                },
              }}
              modifiersClassNames={{
                hasMilestones: 'font-bold',
              }}
              components={{
                DayContent: ({ date }) => {
                  const dateKey = date.toISOString().split('T')[0];
                  const dayMilestones = milestonesGroupedByDate.get(dateKey) || [];
                  
                  return (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <span>{date.getDate()}</span>
                      {dayMilestones.length > 0 && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                          {dayMilestones.slice(0, 3).map((milestone, idx) => (
                            <div
                              key={milestone.id}
                              className={cn(
                                'w-1.5 h-1.5 rounded-full',
                                getMilestoneIndicatorColor(milestone, user?.role || 'client').split(' ')[0]
                              )}
                            />
                          ))}
                          {dayMilestones.length > 3 && (
                            <span className="text-[10px] font-bold ml-0.5">
                              +{dayMilestones.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                },
              }}
            />
            
            {error && (
              <div className="text-center py-8 text-destructive">
                Error loading milestones. Please try again.
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            {sortedMilestones.length > 0 && (
              <div className="space-y-4">
                {sortedMilestones.map((milestone) => {
                  const isUserResp = user?.role && milestone.action_owner === user.role;
                  
                  return (
                    <div
                      key={milestone.id}
                      className={cn(
                        'p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow',
                        isUserResp ? 'border-primary bg-primary/5' : 'border-border'
                      )}
                      onClick={() => {
                        setSelectedDate(new Date(milestone.due_date));
                        setSelectedMilestones([milestone]);
                        setDialogOpen(true);
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{milestone.milestone_name}</h4>
                            {milestone.is_completed && (
                              <Badge variant="default">Completed</Badge>
                            )}
                            {!milestone.is_completed && new Date(milestone.due_date) < new Date() && (
                              <Badge variant="destructive">Overdue</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>{milestone.job_title} ({milestone.job_public_id})</p>
                            <p>Due: {format(new Date(milestone.due_date), 'MMM d, yyyy')}</p>
                            <p>Action: {milestone.action_owner === 'client' ? 'Client' : 'Vendor'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Details Dialog */}
      {selectedDate && (
        <MilestoneDetailsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          milestones={selectedMilestones}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};
